import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, ArrowLeft, Globe, AlertTriangle, Plus, Upload, Trash2, ArrowRight, Download, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatEdgeFunctionError } from '@/lib/edge-function-error';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from '@/components/ui/checkbox';
import { Helmet } from '@/components/Helmet';
import { TierSelection, TIER_PRICES, CANCELLATION_FEES, type TierType } from '@/components/portal/TierSelection';
import { CurrencySelector } from '@/components/portal/CurrencySelector';
import { useCurrency } from '@/hooks/use-currency';
import { CurrencyDock } from '@/components/portal/CurrencyDock';
import { getClientReferenceId } from '@/lib/rewardful';
import { SecureWebsiteOnboardingDialog } from '@/components/portal/SecureWebsiteOnboardingDialog';
import { openExternalUrl } from '@/lib/navigation';
import { usePortfolioPricingApplication } from '@/hooks/use-portfolio-pricing-application';
import { useReviewedPricingSchedule } from '@/hooks/use-reviewed-pricing-schedule';
import { PortfolioPricingStatusCard } from '@/components/portal/PortfolioPricingStatusCard';
import {
  FREE_TRIAL_DAYS,
  FREE_TRIAL_DOMAIN_LIMIT,
  FREE_TRIAL_LABEL,
  FREE_TRIAL_PLANS,
  formatTrialEndLabel,
} from '@/lib/trial';
import {
  REVIEWED_PRICING_SCHEDULE_TITLE,
  SERVICE_AGREEMENT_VERSION,
} from '@/lib/serviceAgreement';
import {
  buildPortfolioPricingAnalyticsProps,
  capturePortfolioPricingEvent,
} from '@/lib/portfolioPricingAnalytics';
import { ensureConfiguredPublicTierPriceId, PUBLIC_TIER_LABELS } from '../../../shared/subscriptionPlans';
import {
  BASIC_HOSTING_MONTHLY_PRICE_AUD,
  HOSTING_BASE_PLAN,
  isSecurityAddonTier,
} from '../../../shared/domainPlans';
import {
  getHostedLifecycleMeta,
  resolveHostedAutomation,
  resolveHostedPluginInstall,
  resolveHostedLifecycle,
  SHADOWSCAN_WORDPRESS_PLUGIN,
} from '@/lib/wordpressLifecycle';

const PLATFORM_OPTIONS = [
  { value: 'wordpress', label: 'WordPress' },
  { value: 'drupal', label: 'Drupal' },
  { value: 'magento', label: 'Magento' },
  { value: 'joomla', label: 'Joomla' },
  { value: 'laravel', label: 'Laravel' },
  { value: 'symfony', label: 'Symfony' },
];

const calculateProRata = (monthlyTotal: number) => {
  const now = new Date();
  const firstOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysRemaining = Math.ceil((firstOfNextMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const proRata = Math.round((monthlyTotal * 100 * daysRemaining) / daysInMonth) / 100;
  return { amount: proRata, daysRemaining, daysInMonth };
};

const calculateProRataFromDate = (monthlyTotal: number, baseDate: Date) => {
  const now = baseDate;
  const firstOfNextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const daysInMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
  const daysRemaining = Math.max(1, Math.ceil((firstOfNextMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  const proRata = Math.round((monthlyTotal * 100 * daysRemaining) / daysInMonth) / 100;
  return { amount: proRata, daysRemaining, daysInMonth };
};

const isValidDomainInput = (value: string) => {
  if (!value) return false;
  const candidate = value.trim();
  try {
    const url = new URL(candidate.includes('://') ? candidate : `https://${candidate}`);
    if (!url.hostname || !url.hostname.includes('.')) return false;
    return /^[a-z0-9.-]+$/i.test(url.hostname);
  } catch {
    return false;
  }
};

const normalizeDomainInput = (value: string) => {
  if (!value) return '';
  const candidate = value.trim();
  try {
    const url = new URL(candidate.includes('://') ? candidate : `https://${candidate}`);
    return url.hostname.toLowerCase();
  } catch {
    return '';
  }
};

const domainSchema = z.object({
  domainName: z
    .string()
    .min(3, 'Domain name is required')
    .max(255)
    .refine(isValidDomainInput, 'Enter a valid domain or URL (e.g., example.com or https://example.com).'),
  platform: z.string().min(1, 'Platform is required'),
});



interface DomainEntry {
  domainName: string;
  platform: string;
}

const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  const pushRowIfNotEmpty = () => {
    const candidate = [...row, field];
    const isEmpty = candidate.every((c) => (c ?? "").trim() === "");
    if (!isEmpty) rows.push(candidate);
    row = [];
    field = "";
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        const next = text[i + 1];
        if (next === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      row.push(field);
      field = "";
      continue;
    }

    if (char === "\n") {
      pushRowIfNotEmpty();
      continue;
    }

    if (char === "\r") {
      continue;
    }

    field += char;
  }

  // Flush last row
  if (row.length > 0 || field.trim() !== "") {
    pushRowIfNotEmpty();
  }

  return rows;
};

const normalizeHeader = (header: string) => header.trim().toLowerCase().replace(/\s+/g, "_");

const getHeaderIndex = (headers: string[], aliases: string[]) => {
  const normalized = headers.map(normalizeHeader);
  const normalizedAliases = aliases.map(normalizeHeader);
  return normalized.findIndex((h) => normalizedAliases.includes(h));
};

export default function AddDomain() {
  const { user, loading } = useAuth();
  const { isImpersonating, activeCustomerId } = useImpersonation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { checking } = useRequireMfa();
  const { formatCurrency } = useCurrency();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('single');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<'single' | 'bulk' | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(null);
  const [pendingInvoiceUrl, setPendingInvoiceUrl] = useState<string | null>(null);
  const [checkingInvoice, setCheckingInvoice] = useState(false);
  const [resumingDomainId, setResumingDomainId] = useState<string | null>(null);
  const [domainPurchaseRequestByDomainId, setDomainPurchaseRequestByDomainId] = useState<Record<string, string>>({});
  const [pairingDialogOpen, setPairingDialogOpen] = useState(false);
  const [pairingDialogCode, setPairingDialogCode] = useState('');
  
  // Tier selection state
  const [selectedTier, setSelectedTier] = useState<TierType | null>(null);
  const [selectedBasePlan, setSelectedBasePlan] = useState<typeof HOSTING_BASE_PLAN | null>(null);
  const [step, setStep] = useState<'tier' | 'domain' | 'secure'>('tier');
  const [accountKind, setAccountKind] = useState<'standard' | 'dev'>('standard');
  const [devPricing, setDevPricing] = useState<{ priceId: string; priceCents: number; currency: string } | null>(null);
  const [reviewedPricingMinActiveDomains, setReviewedPricingMinActiveDomains] = useState<number | null>(null);
  const [pairingLinks, setPairingLinks] = useState<Record<string, string>>({});
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingDomain, setOnboardingDomain] = useState<any | null>(null);
  const [secureDomains, setSecureDomains] = useState<any[]>([]);
  const [hostedMigrationRequested, setHostedMigrationRequested] = useState(false);
  const [upgradeTargetDomain, setUpgradeTargetDomain] = useState<any | null>(null);

  // Single domain state
  const [domainName, setDomainName] = useState('');
  const [platform, setPlatform] = useState('wordpress');

  // Bulk domain state
  const [bulkDomains, setBulkDomains] = useState<DomainEntry[]>([
    { domainName: '', platform: 'wordpress' }
  ]);
  const csvFileInputRef = useRef<HTMLInputElement | null>(null);

  const isDevAccount = accountKind === 'dev';
  const hostingBaseSelected = selectedBasePlan === HOSTING_BASE_PLAN;
  const currentTierPrice = selectedTier ? TIER_PRICES[selectedTier].price_per_domain : 0;
  const currentPlanPrice = isDevAccount
    ? (devPricing?.priceCents || 0) / 100
    : hostingBaseSelected
      ? BASIC_HOSTING_MONTHLY_PRICE_AUD + currentTierPrice
      : currentTierPrice;
  const selectedPlanLabel = isDevAccount
    ? 'Reviewed pricing'
    : hostingBaseSelected
      ? selectedTier
        ? `${PUBLIC_TIER_LABELS.basic} + ${PUBLIC_TIER_LABELS[selectedTier]}`
        : PUBLIC_TIER_LABELS.basic
      : selectedTier
        ? PUBLIC_TIER_LABELS[selectedTier]
        : null;
  const estimatedLabel = 'Estimated conversion; billed in AUD.';
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState<"idle" | "checking" | "applied" | "invalid">("idle");
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  const [couponDetails, setCouponDetails] = useState<{
    couponId: string;
    promotion_code?: string | null;
    percent_off: number | null;
    amount_off: number | null;
    discounted_total: number;
    discounted_unit: number;
    base_total: number;
    quantity: number;
    tier: string;
    is_bulk: boolean;
  } | null>(null);
  const domainOwnerUserId = isImpersonating ? activeCustomerId : user?.id;
  const { data: portfolioPricingApplication } = usePortfolioPricingApplication(
    isImpersonating ? null : user?.id,
    Boolean(user?.id) && !checking && !loading && !isImpersonating,
  );
  const { data: latestReviewedPricingSchedule } = useReviewedPricingSchedule(
    isImpersonating ? null : user?.id,
    Boolean(user?.id) && !checking && !loading && !isImpersonating,
  );

  const maybeCaptureFirstDomainAddedAfterApplication = async (insertedCount: number) => {
    if (!portfolioPricingApplication?.submitted_at || !domainOwnerUserId || isImpersonating) {
      return;
    }

    try {
      const { count, error } = await supabase
        .from('domains')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', domainOwnerUserId)
        .gte('created_at', portfolioPricingApplication.submitted_at);

      if (error) {
        console.error('Failed to determine first domain after portfolio pricing application:', error);
        return;
      }

      if (count === insertedCount) {
        capturePortfolioPricingEvent('portfolio_pricing_first_domain_added_after_application', {
          page: 'portal_add_domain',
          domain_count: insertedCount,
          ...buildPortfolioPricingAnalyticsProps({
            submissionSource: portfolioPricingApplication.submission_source,
            status: portfolioPricingApplication.status,
            accountKind,
            campaignSource: portfolioPricingApplication.signup_source,
            approximatePortfolioSize: portfolioPricingApplication.approximate_portfolio_size,
            currentSiteCount: portfolioPricingApplication.current_site_count,
            expectedSiteCount: portfolioPricingApplication.expected_site_count,
          }),
        });
      }
    } catch (error) {
      console.error('Failed to capture first domain after application event:', error);
    }
  };

  const refreshReviewedPricingSchedule = async () => {
    if (!isDevAccount || !user?.id || isImpersonating) return;
    await queryClient.invalidateQueries({ queryKey: ['reviewed-pricing-schedule', user.id] });
  };

  const refreshCustomerPortalStateAfterTrialStart = async () => {
    if (!user?.id || isImpersonating) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['domains', user.id] }),
      queryClient.invalidateQueries({ queryKey: ['domains'] }),
      queryClient.invalidateQueries({ queryKey: ['profile', user.id] }),
      refreshReviewedPricingSchedule(),
    ]);
  };

  useEffect(() => {
    if (!domainOwnerUserId) return;
    let active = true;

    const loadAccountPricing = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('account_kind, default_dev_price_cents, default_dev_price_currency, default_dev_price_id, reviewed_pricing_min_active_domains')
        .eq('user_id', domainOwnerUserId)
        .maybeSingle();

      if (!active) return;
      if (error) {
        console.error('Failed to load account pricing profile:', error);
        return;
      }

      const nextKind = data?.account_kind === 'dev' ? 'dev' : 'standard';
      setAccountKind(nextKind);
      if (nextKind === 'dev') {
        if (data?.default_dev_price_id && typeof data.default_dev_price_cents === 'number') {
          setDevPricing({
            priceId: data.default_dev_price_id,
            priceCents: data.default_dev_price_cents,
            currency: data.default_dev_price_currency || 'AUD',
          });
        } else {
          setDevPricing(null);
        }
        setReviewedPricingMinActiveDomains(
          typeof data?.reviewed_pricing_min_active_domains === 'number' && data.reviewed_pricing_min_active_domains > 0
            ? data.reviewed_pricing_min_active_domains
            : null,
        );
        setStep((prev) => (prev === 'tier' ? 'domain' : prev));
      } else {
        setDevPricing(null);
        setReviewedPricingMinActiveDomains(null);
      }
    };

    void loadAccountPricing();
    return () => {
      active = false;
    };
  }, [domainOwnerUserId]);

  useEffect(() => {
    if (loading || checking || user) return;
    navigate('/portal/sign-up', {
      replace: true,
      state: { from: `${location.pathname}${location.search}` },
    });
  }, [checking, loading, location.pathname, location.search, navigate, user]);

  useEffect(() => {
    if (loading || checking || !user || isDevAccount) return;

    const planParam = searchParams.get('plan');
    const baseParam = searchParams.get('base');
    const addonParam = searchParams.get('addon');

    if (baseParam === HOSTING_BASE_PLAN) {
      setSelectedBasePlan(HOSTING_BASE_PLAN);
      setSelectedTier(isSecurityAddonTier(addonParam) ? addonParam : null);
      setStep('domain');
      return;
    }

    if (planParam === 'essential' || planParam === 'premium') {
      setSelectedBasePlan(null);
      setSelectedTier(planParam);
      setStep('domain');
    }
  }, [checking, isDevAccount, loading, searchParams, user]);

  useEffect(() => {
    const upgradeDomainId = searchParams.get('domain_id');
    if (!upgradeDomainId || !domainOwnerUserId) {
      setUpgradeTargetDomain(null);
      return;
    }

    let active = true;
    const loadUpgradeDomain = async () => {
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('id', upgradeDomainId)
        .eq('user_id', domainOwnerUserId)
        .maybeSingle();

      if (!active) return;
      if (error) {
        toast.error('Unable to load the selected hosted site.');
        return;
      }

      setUpgradeTargetDomain(data || null);
      if (data) {
        setDomainName(data.domain_name || '');
        setPlatform(data.platform || 'wordpress');
        setActiveTab('single');
        setStep('domain');
      }
    };

    void loadUpgradeDomain();
    return () => {
      active = false;
    };
  }, [domainOwnerUserId, searchParams]);

  const isPaymentNeeded = (status?: string | null) =>
    status === 'payment_needed' || status === 'payment_incomplete' || status === 'pending_payment';
  const qualifiesForSelfServeTrial =
    !isImpersonating &&
    !hostingBaseSelected &&
    (isDevAccount || (!!selectedTier && FREE_TRIAL_PLANS.some((plan) => plan === selectedTier)));

  const ensureTrialPaymentMethodReady = async () => {
    const session = await ensureValidSession();
    const { data: paymentState, error: paymentError } = await supabase.functions.invoke('check-payment-method', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (paymentError) {
      throw paymentError;
    }

    if (paymentState?.hasPaymentMethod) {
      return true;
    }

    const { data: portalData, error: portalError } = await supabase.functions.invoke('customer-portal', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (portalError) {
      throw portalError;
    }

    if (portalData?.url) {
      toast.info(`Add a payment method first, then return to start your ${FREE_TRIAL_LABEL}.`);
      openExternalUrl(portalData.url, { newTab: false });
      return false;
    }

    throw new Error('Unable to open billing setup. Please try again.');
  };

  const moveToSecureStep = (domains: any[], autoOpen = true) => {
    if (domains.length > 0) {
      setSecureDomains(domains);
      setOnboardingDomain(domains[0]);
    }
    setStep('secure');
    if (autoOpen) setOnboardingOpen(true);
    const domainIds = domains.map((domain) => domain.id);
    if (domainIds.length > 0) {
      localStorage.setItem(
        'add-domain-secure',
        JSON.stringify({ domainIds, ts: Date.now() }),
      );
    }
  };

  const loadSecureDomains = async (domainIds: string[]) => {
    if (!domainOwnerUserId || domainIds.length === 0) return;
    const { data, error } = await supabase
      .from('domains')
      .select('id, domain_name, status, wp_admin_url, tier, trial_ends_at, billing_starts_at, trial_started_at')
      .in('id', domainIds)
      .eq('user_id', domainOwnerUserId);
    if (error) {
      toast.error('Unable to load domain details.');
      return;
    }
    const { data: managedSites, error: managedSitesError } = await supabase
      .from('managed_sites')
      .select('id, domain_id, wp_admin_url, paired_at, last_heartbeat_at, plugin_version, can_install_plugins, site_token_hash, plugin_install_source, plugin_install_status, plugin_install_attempted_at, plugin_install_verified_at, plugin_install_error, hosted_automation_status, hosted_automation_last_run_at, hosted_automation_error, hosted_is_migration, hosted_migration_confirmed_at, hosted_migration_confirmed_by')
      .in('domain_id', domainIds)
      .eq('user_id', domainOwnerUserId);
    if (managedSitesError) {
      toast.error('Unable to load WordPress connector details.');
      return;
    }
    if (data && data.length > 0) {
      const managedSiteByDomainId = new Map(
        (managedSites || []).map((managedSite) => [managedSite.domain_id, managedSite]),
      );
      const enrichedDomains = data.map((domain) => ({
        ...domain,
        managed_site: managedSiteByDomainId.get(domain.id) || null,
      }));
      setSecureDomains(enrichedDomains);
      setOnboardingDomain(enrichedDomains[0]);
      if (enrichedDomains[0]?.tier && (enrichedDomains[0].tier === 'basic' || enrichedDomains[0].tier === 'essential' || enrichedDomains[0].tier === 'premium')) {
        setSelectedTier(enrichedDomains[0].tier);
      }
    }
  };

  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam !== 'secure') return;
    const domainId = searchParams.get('domain_id');
    const bulkParam = searchParams.get('bulk');
    const ids = [
      ...(domainId ? [domainId] : []),
      ...(bulkParam ? bulkParam.split(',').map((id) => id.trim()).filter(Boolean) : []),
    ];
    if (ids.length > 0) {
      void loadSecureDomains(ids);
    }
    setStep('secure');
    setOnboardingOpen(true);
  }, [searchParams, domainOwnerUserId]);

  useEffect(() => {
    const stored = localStorage.getItem('add-domain-secure');
    if (!stored || !domainOwnerUserId) return;
    try {
      const payload = JSON.parse(stored) as { domainIds?: string[] };
      if (payload?.domainIds?.length) {
        void loadSecureDomains(payload.domainIds);
        setStep('secure');
        setOnboardingOpen(true);
      }
    } catch {
      // Ignore parsing errors.
    } finally {
      localStorage.removeItem('add-domain-secure');
    }
  }, [domainOwnerUserId]);

  useEffect(() => {
    const stepParam = searchParams.get('step');
    const hasSecureState = localStorage.getItem('add-domain-secure');
    if (stepParam === 'secure' || hasSecureState) return;
    setStep(isDevAccount ? 'domain' : 'tier');
    setSecureDomains([]);
    setOnboardingDomain(null);
    setOnboardingOpen(false);
  }, [isDevAccount, searchParams]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data as { type?: string; domainId?: string; bulkIds?: string[] };
      if (payload?.type !== 'checkout_complete') return;
      const ids = [
        ...(payload.domainId ? [payload.domainId] : []),
        ...(payload.bulkIds || []),
      ].filter(Boolean);
      if (ids.length > 0) {
        void loadSecureDomains(ids);
      }
      setStep('secure');
      setOnboardingOpen(true);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [user]);

  const handleOpenOnboarding = (domain: any) => {
    setOnboardingDomain(domain);
    setOnboardingOpen(true);
  };

  const handleOnboardingOpenChange = (open: boolean) => {
    setOnboardingOpen(open);
  };

  const handleGeneratePairingLink = async (domain: any, altchaToken?: string | null) => {
    if (!domain?.id) return;
    if (!altchaToken) {
      toast.error('Please complete verification before generating a pairing code.');
      return;
    }
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pair-start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-ss-auth': 'user',
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            domainId: domain.id,
            siteUrl: domain.domain_name,
            portalBaseUrl: window.location.origin,
            altchaToken,
          }),
        },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `pair-start failed: ${res.status}`);
      }
      const data = await res.json();
      const pairingCode = data?.pairing_code;
      if (!pairingCode) {
        toast.error('Unable to generate pairing code.');
        return;
      }
      const pairingLink = `${window.location.origin}/portal?pairing_code=${encodeURIComponent(pairingCode)}`;
      setPairingLinks((prev) => ({ ...prev, [domain.id]: pairingLink }));
      const managedSitePatch = {
        id: data?.site_id ?? domain?.managed_site?.id ?? null,
        wp_admin_url: domain?.managed_site?.wp_admin_url ?? domain?.wp_admin_url ?? null,
        paired_at: domain?.managed_site?.paired_at ?? null,
        last_heartbeat_at: domain?.managed_site?.last_heartbeat_at ?? null,
        plugin_version: domain?.managed_site?.plugin_version ?? null,
        can_install_plugins: domain?.managed_site?.can_install_plugins ?? null,
        site_token_hash: domain?.managed_site?.site_token_hash ?? null,
        plugin_install_source: domain?.managed_site?.plugin_install_source ?? null,
        plugin_install_status: domain?.managed_site?.plugin_install_status ?? null,
        plugin_install_attempted_at: domain?.managed_site?.plugin_install_attempted_at ?? null,
        plugin_install_verified_at: domain?.managed_site?.plugin_install_verified_at ?? null,
        plugin_install_error: domain?.managed_site?.plugin_install_error ?? null,
        hosted_automation_status: domain?.managed_site?.hosted_automation_status ?? null,
        hosted_automation_last_run_at: domain?.managed_site?.hosted_automation_last_run_at ?? null,
        hosted_automation_error: domain?.managed_site?.hosted_automation_error ?? null,
        hosted_is_migration: domain?.managed_site?.hosted_is_migration ?? false,
        hosted_migration_confirmed_at: domain?.managed_site?.hosted_migration_confirmed_at ?? null,
        hosted_migration_confirmed_by: domain?.managed_site?.hosted_migration_confirmed_by ?? null,
      };
      setSecureDomains((prev) =>
        prev.map((entry) =>
          entry.id === domain.id
            ? { ...entry, managed_site: { ...(entry.managed_site || {}), ...managedSitePatch } }
            : entry,
        ),
      );
      setOnboardingDomain((prev) =>
        prev?.id === domain.id
          ? { ...prev, managed_site: { ...(prev.managed_site || {}), ...managedSitePatch } }
          : prev,
      );
      // Only open the separate pairing dialog if the onboarding dialog isn't already open
      // (the onboarding dialog shows the pairing code inline)
      if (!onboardingOpen) {
        setPairingDialogCode(pairingCode);
        setPairingDialogOpen(true);
      }
    } catch (err: unknown) {
      console.error('pair-start failed:', err);
      toast.error(await formatEdgeFunctionError(err, 'Unable to generate pairing link.'));
    }
  };

  const handleCopyPairingCode = async () => {
    if (!pairingDialogCode) return;
    try {
      await navigator.clipboard.writeText(pairingDialogCode);
      toast.success('Pairing code copied to clipboard.');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = pairingDialogCode;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success('Pairing code copied to clipboard.');
      } catch {
        toast.error('Unable to copy pairing code. Select and copy manually.');
      } finally {
        textarea.remove();
      }
    }
  };

  const createImpersonationPurchaseRequest = async (domains: DomainEntry[]) => {
    if (!activeCustomerId) {
      throw new Error('No active customer selected for impersonation.');
    }
    const session = await ensureValidSession();
    const tier = isDevAccount ? 'dev' : (hostingBaseSelected ? 'basic' : (selectedTier || 'premium'));
    const { data, error } = await supabase.functions.invoke('admin-create-domain-purchase-request', {
      body: {
        customerUserId: activeCustomerId,
        domains: domains.map((domain) => ({
          domainName: domain.domainName,
          platform: domain.platform,
        })),
        tier,
        base: hostingBaseSelected ? HOSTING_BASE_PLAN : null,
        addon: hostingBaseSelected && selectedTier ? selectedTier : null,
        isMigration: hostedMigrationRequested,
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    if (error || data?.error) {
      throw new Error(error?.message || data?.error || 'Unable to create purchase request.');
    }
    return data as {
      requestId: string;
      domains: any[];
      expiresAt: string;
      emailSent?: boolean;
    };
  };

  const handleResumePayment = async (domainId: string) => {
    setResumingDomainId(domainId);
    try {
      const currentDomain = secureDomains.find((domain) => domain?.id === domainId) || (onboardingDomain?.id === domainId ? onboardingDomain : null);
      if (currentDomain?.status === 'trial') {
        toast.info('This domain is in the 7-day free trial. No payment is due until the trial ends.');
        return;
      }

      if (isImpersonating) {
        const fallbackRequestId = secureDomains.find((domain) => domain?.id === domainId)?.pending_purchase_request?.id;
        const requestId = domainPurchaseRequestByDomainId[domainId] || fallbackRequestId;
        if (!requestId) {
          throw new Error('No pending purchase request was found for this domain in this session.');
        }
        const session = await ensureValidSession();
        const { data, error } = await supabase.functions.invoke('admin-resend-domain-purchase-request', {
          body: { requestId },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (error || data?.error) throw error || new Error(data?.error || 'Unable to resend purchase request.');
        if (typeof data?.requestId === 'string' && data.requestId.length > 0) {
          setDomainPurchaseRequestByDomainId((prev) => {
            const next = { ...prev };
            for (const [id, mappedRequestId] of Object.entries(prev)) {
              if (mappedRequestId === requestId) {
                next[id] = data.requestId;
              }
            }
            return next;
          });
          setSecureDomains((prev) =>
            prev.map((domain) => (
              domain?.pending_purchase_request?.id === requestId
                ? {
                    ...domain,
                    pending_purchase_request: {
                      ...(domain.pending_purchase_request || {}),
                      id: data.requestId,
                      status: 'pending',
                      expires_at: data?.expiresAt || domain.pending_purchase_request?.expires_at || null,
                    },
                  }
                : domain
            )),
          );
          setOnboardingDomain((prev) => (
            prev?.pending_purchase_request?.id === requestId
              ? {
                  ...prev,
                  pending_purchase_request: {
                    ...(prev.pending_purchase_request || {}),
                    id: data.requestId,
                    status: 'pending',
                    expires_at: data?.expiresAt || prev.pending_purchase_request?.expires_at || null,
                  },
                }
              : prev
          ));
        }
        toast.success('Customer payment email resent.');
      } else {
        const { data, error } = await supabase.functions.invoke('resume-checkout', {
          body: { domainId },
        });
        if (error) throw error;
        if (data?.url) {
          window.location.assign(data.url);
          toast.success('Redirecting to checkout to complete payment.');
        } else {
          toast.error('Could not start checkout. Please try again.');
        }
      }
    } catch (err: any) {
      console.error('Resume checkout failed:', err);
      toast.error(err?.message || (isImpersonating ? 'Unable to resend purchase email. Please try again.' : 'Unable to resume payment. Please try again.'));
    } finally {
      setResumingDomainId(null);
    }
  };

  const addBulkRow = () => {
    setBulkDomains([...bulkDomains, { domainName: '', platform: 'wordpress' }]);
  };

  const removeBulkRow = (index: number) => {
    if (bulkDomains.length > 1) {
      setBulkDomains(bulkDomains.filter((_, i) => i !== index));
    }
  };

  const updateBulkDomain = (index: number, field: keyof DomainEntry, value: string) => {
    const updated = [...bulkDomains];
    updated[index] = { ...updated[index], [field]: value };
    setBulkDomains(updated);
  };

  const downloadBulkTemplateCsv = () => {
    const csv =
      "domainName,platform\n" +
      "example.com,wordpress\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "shadowscan-bulk-domains-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const importBulkCsvFile = async (file: File) => {
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) {
        toast.error("CSV must include a header row and at least one domain row.");
        return;
      }

      const headers = rows[0].map((c) => (c ?? "").trim());
      const domainIdx = getHeaderIndex(headers, ["domainName", "domain", "domain_name", "domain name"]);
      const platformIdx = getHeaderIndex(headers, ["platform", "cms", "framework"]);

      const missing: string[] = [];
      if (domainIdx === -1) missing.push("domainName");
      if (platformIdx === -1) missing.push("platform");
      if (missing.length > 0) {
        toast.error(`CSV is missing required columns: ${missing.join(", ")}`);
        return;
      }

      const entries: DomainEntry[] = [];
      for (const row of rows.slice(1)) {
        const entry: DomainEntry = {
          domainName: (row[domainIdx] ?? "").trim(),
          platform: (row[platformIdx] ?? "").trim().toLowerCase(),
        };
        const isEmpty = Object.values(entry).every((v) => v.trim() === "");
        if (isEmpty) continue;
        entries.push(entry);
      }

      if (entries.length === 0) {
        toast.error("No domain rows found in CSV.");
        return;
      }

      const MAX_IMPORT = 500;
      if (entries.length > MAX_IMPORT) {
        toast.error(`CSV contains ${entries.length} rows. Please import ${MAX_IMPORT} domains or fewer at a time.`);
        return;
      }

      setBulkDomains([...entries, { domainName: "", platform: "wordpress" }]);
      toast.success(`Imported ${entries.length} domain${entries.length === 1 ? "" : "s"} from CSV.`);
    } catch (err) {
      console.error("CSV import failed:", err);
      toast.error("Could not import CSV. Please check the file format and try again.");
    } finally {
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = "";
      }
    }
  };

  const getValidBulkDomains = () => {
    return bulkDomains.filter(d => 
      d.domainName.trim() && 
      d.platform.trim()
    );
  };

  useEffect(() => {
    if (isDevAccount) {
      setSelectedBasePlan(null);
      setCouponStatus("idle");
      setCouponDetails(null);
      setCouponMessage(null);
      return;
    }
    if (!couponCode.trim()) {
      setCouponStatus("idle");
      setCouponDetails(null);
      setCouponMessage(null);
      return;
    }
    setCouponStatus("idle");
    setCouponDetails(null);
    setCouponMessage(null);
  }, [isDevAccount, selectedTier, activeTab, bulkDomains, couponCode]);

  const handleApplyCoupon = async () => {
    if (isImpersonating) {
      toast.info('Coupons are not available in impersonation setup.');
      return;
    }
    if (isDevAccount) {
      toast.info("Coupons are not available for dev accounts.");
      return;
    }
    if (hostingBaseSelected) {
      toast.info('Coupons are not available for Basic Hosting checkout paths.');
      return;
    }
    const trimmed = couponCode.trim();
    if (!trimmed) {
      setCouponStatus("idle");
      setCouponDetails(null);
      setCouponMessage("Enter a coupon code.");
      return;
    }
    if (!selectedTier) {
      toast.error("Select a plan before applying a coupon.");
      return;
    }

    const quantity = activeTab === "bulk" ? getValidBulkDomains().length : 1;
    if (activeTab === "bulk" && quantity === 0) {
      toast.error("Add at least one domain before applying a coupon.");
      return;
    }

    setCouponStatus("checking");
    setCouponMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke("validate-coupon", {
        body: { couponId: trimmed, tier: selectedTier, quantity },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || "Unable to validate coupon.");

      if (data?.valid) {
        setCouponDetails(data);
        setCouponStatus("applied");
        setCouponMessage("Coupon applied.");
      } else {
        setCouponDetails(null);
        setCouponStatus("invalid");
        setCouponMessage(data?.message || "Coupon is not valid.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to validate coupon.";
      setCouponDetails(null);
      setCouponStatus("invalid");
      setCouponMessage(message);
    }
  };

  const handleTierSelect = (tier: TierType) => {
    setSelectedBasePlan(null);
    setSelectedTier(tier);
  };

  const handleHostingSelection = (addonTier: TierType | null) => {
    setSelectedBasePlan(HOSTING_BASE_PLAN);
    setSelectedTier(addonTier);
    setCouponStatus("idle");
    setCouponDetails(null);
    setCouponMessage(null);
  };

  const handleContinueToForm = () => {
    if (isDevAccount) {
      setStep('domain');
      return;
    }
    if (!selectedTier && !hostingBaseSelected) {
      toast.error('Please select a plan');
      return;
    }
    setStep('domain');
  };

  const handleSingleSubmitClick = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      domainSchema.parse({ domainName, platform });
      if (platform !== 'wordpress') {
        toast.error('Only WordPress is supported today. Other platforms are coming soon.');
        return;
      }
      setPendingSubmit('single');
      setAgreedToTerms(false);
      setShowConfirmDialog(true);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
      }
    }
  };

  const handleBulkSubmitClick = async (e: React.FormEvent) => {
    e.preventDefault();
    if (upgradeTargetDomain) {
      toast.error('Hosted site upgrades use the single-domain flow.');
      return;
    }
    const validDomains = getValidBulkDomains();
    
    if (validDomains.length === 0) {
      toast.error('Please fill in at least one complete domain entry');
      return;
    }

    try {
      for (const domain of validDomains) {
        domainSchema.parse(domain);
      }
      const nonWordpress = validDomains.find((domain) => domain.platform !== 'wordpress');
      if (nonWordpress) {
        toast.error('Only WordPress is supported today. Other platforms are coming soon.');
        return;
      }
      setPendingSubmit('bulk');
      setAgreedToTerms(false);
      setShowConfirmDialog(true);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
      }
    }
  };

  const handleConfirmAdd = async () => {
    if (!agreedToTerms) {
      toast.error('Please agree to the service terms to continue');
      return;
    }

    setShowConfirmDialog(false);
    setIsLoading(true);
    setPendingInvoiceId(null);
    setPendingInvoiceUrl(null);

    if (pendingSubmit === 'single') {
      await processSingleSubmit();
    } else if (pendingSubmit === 'bulk') {
      await processBulkSubmit();
    }

    setPendingSubmit(null);
  };

  const ensureValidSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.expires_at && session.expires_at * 1000 - Date.now() > 60_000) {
      return session;
    }
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      toast.error('Your session expired. Please log in again to continue checkout.');
      navigate('/portal/auth');
      throw new Error('Please log in again to continue checkout.');
    }
    return data.session;
  };

  const processSingleSubmit = async () => {
    try {
      if (!domainOwnerUserId) {
        throw new Error('Unable to resolve domain owner for this request.');
      }
      if (isDevAccount && !devPricing?.priceId) {
        throw new Error('Dev account pricing is not configured. Contact an administrator.');
      }
      if (qualifiesForSelfServeTrial) {
        const readyForTrial = await ensureTrialPaymentMethodReady();
        if (!readyForTrial) {
          setIsLoading(false);
          return;
        }
      }
      const normalizedDomain = normalizeDomainInput(domainName);
      if (!normalizedDomain) {
        toast.error('Enter a valid domain or URL (e.g., example.com or https://example.com).');
        setIsLoading(false);
        return;
      }
      if (!upgradeTargetDomain) {
        setDomainName(normalizedDomain);
        const { data: existingDomains } = await supabase
          .from('domains')
          .select('domain_name')
          .eq('user_id', domainOwnerUserId)
          .is('deleted_at', null)
          .ilike('domain_name', normalizedDomain);

        if (existingDomains && existingDomains.length > 0) {
          toast.error(`Domain "${normalizedDomain}" is already in your account.`);
          setIsLoading(false);
          return;
        }
      }

      const selectedTierPriceId = !isDevAccount && selectedTier
        ? ensureConfiguredPublicTierPriceId(selectedTier, TIER_PRICES[selectedTier].price_id)
        : null;
      const selectedBasePriceId = !isDevAccount && hostingBaseSelected
        ? ensureConfiguredPublicTierPriceId('basic', TIER_PRICES.basic.price_id)
        : null;
      const checkoutTier = isDevAccount ? 'dev' : (hostingBaseSelected ? 'basic' : selectedTier);

      if (isImpersonating) {
        const request = await createImpersonationPurchaseRequest([
          { domainName: upgradeTargetDomain?.domain_name || normalizedDomain, platform },
        ]);
        const requestedDomains = (Array.isArray(request.domains) ? request.domains : []).map((row: any) => ({
          ...row,
          pending_purchase_request: request.requestId
            ? {
                id: request.requestId,
                status: 'pending',
                expires_at: request.expiresAt,
              }
            : null,
        }));
        if (requestedDomains.length === 0) {
          throw new Error('Purchase request was created without any domains.');
        }
        setSecureDomains(requestedDomains);
        setOnboardingDomain(requestedDomains[0] ?? null);
        if (request.requestId) {
          const mapping = requestedDomains.reduce<Record<string, string>>((acc, row: any) => {
            if (row?.id) acc[row.id] = request.requestId;
            return acc;
          }, {});
          setDomainPurchaseRequestByDomainId((prev) => ({ ...prev, ...mapping }));
        }
        if (request.emailSent === false) {
          toast.error('Purchase request created, but the email could not be sent. Use resend from setup.');
        } else {
          toast.success('Purchase request sent to customer.');
        }
        moveToSecureStep(requestedDomains);
        setIsLoading(false);
        return;
      }

      let domain = upgradeTargetDomain;
      if (!domain) {
        const { data, error: insertError } = await (supabase
          .from('domains') as any)
          .insert({
            user_id: domainOwnerUserId,
            domain_name: normalizedDomain,
            platform: platform,
            status: 'payment_needed',
            stripe_price_id: isDevAccount
              ? devPricing?.priceId || null
              : (selectedBasePriceId || selectedTierPriceId),
            tier: isDevAccount ? 'dev' : (hostingBaseSelected ? 'basic' : (selectedTier || null)),
            security_addon_tier: !isDevAccount && hostingBaseSelected && selectedTier ? selectedTier : null,
            security_addon_price_id: !isDevAccount && hostingBaseSelected && selectedTier ? selectedTierPriceId : null,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        domain = data;
      }
      setSecureDomains([domain]);
      setOnboardingDomain(domain);
      if (!upgradeTargetDomain) {
        await maybeCaptureFirstDomainAddedAfterApplication(1);
      }

      if (!upgradeTargetDomain) {
        await supabase.functions.invoke('notify-domain-change', {
          body: { 
            action: 'added',
            domains: [{
              domainName: normalizedDomain,
              status: 'pending',
              createdAt: new Date().toISOString(),
              tier: checkoutTier,
            }],
            userEmail: user!.email,
            notifyOwner: true,
            changes: `Next steps: install the official ${SHADOWSCAN_WORDPRESS_PLUGIN.name} plugin in WordPress, then connect your site using the pairing code in the portal.`,
          },
        });
      }

      // Create subscription (no immediate charge, starts billing on 1st of next month)
      const session = await ensureValidSession();
      const clientReferenceId = getClientReferenceId();
      const appliedCouponId = !isDevAccount && couponStatus === "applied" ? couponDetails?.couponId : null;
      const appliedPromotionCodeId = !isDevAccount && couponStatus === "applied" ? couponDetails?.promotion_code : null;
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-checkout', {
        body: {
          domainId: domain.id,
          domainName: domain.domain_name || normalizedDomain,
          quantity: 1,
          tier: checkoutTier,
          base: hostingBaseSelected ? HOSTING_BASE_PLAN : null,
          addon: hostingBaseSelected && selectedTier ? selectedTier : null,
          isMigration: hostedMigrationRequested,
          clientReferenceId,
          acceptedReviewedPricingSchedule: isDevAccount,
          couponId: appliedCouponId,
          promotionCodeId: appliedPromotionCodeId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (checkoutError) throw checkoutError;

      if (checkoutData?.type === 'trial_started') {
        await refreshCustomerPortalStateAfterTrialStart();
        const trialDomain = {
          ...domain,
          status: 'trial',
          trial_started_at: checkoutData.trialStartedAt,
          trial_ends_at: checkoutData.trialEndsAt,
          billing_starts_at: checkoutData.billingStartsAt,
        };
        setSecureDomains([trialDomain]);
        setOnboardingDomain(trialDomain);
        toast.success(`7-day free trial started for up to ${checkoutData.trialDomainLimit || FREE_TRIAL_DOMAIN_LIMIT} domains.`);
        moveToSecureStep([trialDomain]);
        setIsLoading(false);
        return;
      }

      // Open pro-rata invoice for immediate payment if provided
      if (checkoutData?.proRataInvoiceUrl) {
        await refreshReviewedPricingSchedule();
        setPendingInvoiceId(checkoutData.proRataInvoiceId);
        setPendingInvoiceUrl(checkoutData.proRataInvoiceUrl);
        window.location.assign(checkoutData.proRataInvoiceUrl);
        toast.info('Complete the payment, then return to confirm below.');
        moveToSecureStep([domain]);
      }

      if (checkoutData?.requiresProRataPayment) {
        await refreshReviewedPricingSchedule();
        // Wait for manual payment confirmation before redirecting
        setIsLoading(false);
        moveToSecureStep([domain]);
        return;
      }

      if (checkoutData?.redirect && checkoutData?.subscriptionId) {
        await refreshReviewedPricingSchedule();
        await supabase
          .from('domains')
          .update({ stripe_subscription_id: checkoutData.subscriptionId })
          .eq('id', domain.id)
          .eq('user_id', domainOwnerUserId);
        toast.success('Domain added. Continue to secure your site.');
        moveToSecureStep([domain]);
        setIsLoading(false);
        return;
      }

      if (checkoutData?.url) {
        await refreshReviewedPricingSchedule();
        window.location.assign(checkoutData.url);
        toast.success('Redirecting to checkout. Secure your site after payment completes.');
        moveToSecureStep([domain]);
        return;
      }

      throw new Error('Failed to create subscription');
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Something went wrong');
      }
      setIsLoading(false);
    }
  };

  const processBulkSubmit = async () => {
    const validDomains = getValidBulkDomains();
    const normalizedDomains = validDomains
      .map((domain) => ({
        ...domain,
        normalizedName: normalizeDomainInput(domain.domainName),
      }))
      .filter((domain) => domain.normalizedName);

    try {
      if (!domainOwnerUserId) {
        throw new Error('Unable to resolve domain owner for this request.');
      }
      if (isDevAccount && !devPricing?.priceId) {
        throw new Error('Dev account pricing is not configured. Contact an administrator.');
      }
      if (qualifiesForSelfServeTrial && normalizedDomains.length <= FREE_TRIAL_DOMAIN_LIMIT) {
        const readyForTrial = await ensureTrialPaymentMethodReady();
        if (!readyForTrial) {
          setIsLoading(false);
          return;
        }
      }
      setBulkDomains((current) =>
        current.map((domain) => {
          const normalized = normalizeDomainInput(domain.domainName);
          if (!normalized) return domain;
          return { ...domain, domainName: normalized };
        })
      );
      // Check for duplicate domains in the batch itself
      const domainNamesInBatch = normalizedDomains.map((domain) => domain.normalizedName);
      const uniqueDomains = new Set(domainNamesInBatch);
      if (uniqueDomains.size !== domainNamesInBatch.length) {
        toast.error('You have duplicate domains in your list. Please remove duplicates.');
        setIsLoading(false);
        return;
      }

      // Check for existing domains in the database
      const { data: existingDomains } = await supabase
        .from('domains')
        .select('domain_name')
        .eq('user_id', domainOwnerUserId)
        .is('deleted_at', null);

      const existingActiveDomainNames = existingDomains?.map(d => d.domain_name.toLowerCase()) || [];

      const duplicates = normalizedDomains.filter((domain) =>
        existingActiveDomainNames.includes(domain.normalizedName)
      );

      if (duplicates.length > 0) {
        const duplicateNames = duplicates.map((domain) => domain.normalizedName).join(', ');
        toast.error(`These domains already exist in your account: ${duplicateNames}`);
        setIsLoading(false);
        return;
      }

      if (isImpersonating) {
        const request = await createImpersonationPurchaseRequest(
          normalizedDomains.map((domain) => ({
            domainName: domain.normalizedName,
            platform: domain.platform,
          })),
        );
        const requestedDomains = (Array.isArray(request.domains) ? request.domains : []).map((row: any) => ({
          ...row,
          pending_purchase_request: request.requestId
            ? {
                id: request.requestId,
                status: 'pending',
                expires_at: request.expiresAt,
              }
            : null,
        }));
        if (requestedDomains.length === 0) {
          throw new Error('Purchase request was created without any domains.');
        }
        setSecureDomains(requestedDomains);
        setOnboardingDomain(requestedDomains[0] ?? null);
        if (request.requestId) {
          const mapping = requestedDomains.reduce<Record<string, string>>((acc, row: any) => {
            if (row?.id) acc[row.id] = request.requestId;
            return acc;
          }, {});
          setDomainPurchaseRequestByDomainId((prev) => ({ ...prev, ...mapping }));
        }
        if (request.emailSent === false) {
          toast.error('Purchase request created, but the email could not be sent. Use resend from setup.');
        } else {
          toast.success('Purchase request sent to customer.');
        }
        moveToSecureStep(requestedDomains);
        setIsLoading(false);
        return;
      }

      // Insert all domains
      const insertedDomains = [];
      const selectedTierPriceId = !isDevAccount && selectedTier
        ? ensureConfiguredPublicTierPriceId(selectedTier, TIER_PRICES[selectedTier].price_id)
        : null;
      const selectedBasePriceId = !isDevAccount && hostingBaseSelected
        ? ensureConfiguredPublicTierPriceId('basic', TIER_PRICES.basic.price_id)
        : null;
      const checkoutTier = isDevAccount ? 'dev' : (hostingBaseSelected ? 'basic' : selectedTier);
      for (const domain of normalizedDomains) {
        const { data, error } = await (supabase.from('domains') as any).insert({
          user_id: domainOwnerUserId,
          domain_name: domain.normalizedName,
          platform: domain.platform,
          status: 'payment_needed',
          stripe_price_id: isDevAccount
            ? devPricing?.priceId || null
            : (selectedBasePriceId || selectedTierPriceId),
          tier: isDevAccount ? 'dev' : (hostingBaseSelected ? 'basic' : (selectedTier || null)),
          security_addon_tier: !isDevAccount && hostingBaseSelected && selectedTier ? selectedTier : null,
          security_addon_price_id: !isDevAccount && hostingBaseSelected && selectedTier ? selectedTierPriceId : null,
        }).select().single();
        
        if (error) throw error;
        if (data) insertedDomains.push(data);
      }

      if (insertedDomains.length === 0) throw new Error('Failed to create domains');

      const domainIds = insertedDomains.map(d => d.id);
      setSecureDomains(insertedDomains);
      setOnboardingDomain(insertedDomains[0] ?? null);
      await maybeCaptureFirstDomainAddedAfterApplication(insertedDomains.length);

      // Send notification email with domain metadata only.
      await supabase.functions.invoke('notify-domain-change', {
        body: { 
          action: 'added',
          domains: normalizedDomains.map((domain) => ({
            domainName: domain.normalizedName,
            status: 'pending',
            createdAt: new Date().toISOString(),
            tier: checkoutTier,
          })),
          userEmail: user!.email,
          notifyOwner: true,
          changes: `Next steps: install the official ${SHADOWSCAN_WORDPRESS_PLUGIN.name} plugin in WordPress, then connect your site using the pairing code in the portal.`,
        },
      });

      // Create subscription for all domains (no immediate charge, starts billing on 1st of next month)
      const session = await ensureValidSession();
      const clientReferenceId = getClientReferenceId();
      const appliedCouponId = !isDevAccount && couponStatus === "applied" ? couponDetails?.couponId : null;
      const appliedPromotionCodeId = !isDevAccount && couponStatus === "applied" ? couponDetails?.promotion_code : null;
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-checkout', {
        body: { 
          domainId: domainIds[0], 
          domainName: normalizedDomains[0].normalizedName,
          quantity: normalizedDomains.length,
          bulkDomainIds: domainIds,
          tier: checkoutTier,
          base: hostingBaseSelected ? HOSTING_BASE_PLAN : null,
          addon: hostingBaseSelected && selectedTier ? selectedTier : null,
          isMigration: hostedMigrationRequested,
          clientReferenceId,
          acceptedReviewedPricingSchedule: isDevAccount,
          couponId: appliedCouponId,
          promotionCodeId: appliedPromotionCodeId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (checkoutError) throw checkoutError;

      if (checkoutData?.type === 'trial_started') {
        await refreshCustomerPortalStateAfterTrialStart();
        const trialDomains = insertedDomains.map((entry) => ({
          ...entry,
          status: 'trial',
          trial_started_at: checkoutData.trialStartedAt,
          trial_ends_at: checkoutData.trialEndsAt,
          billing_starts_at: checkoutData.billingStartsAt,
        }));
        setSecureDomains(trialDomains);
        setOnboardingDomain(trialDomains[0] ?? null);
        toast.success(`7-day free trial started for up to ${checkoutData.trialDomainLimit || FREE_TRIAL_DOMAIN_LIMIT} domains.`);
        moveToSecureStep(trialDomains);
        setIsLoading(false);
        return;
      }

      if (checkoutData?.proRataInvoiceUrl) {
        await refreshReviewedPricingSchedule();
        setPendingInvoiceId(checkoutData.proRataInvoiceId);
        setPendingInvoiceUrl(checkoutData.proRataInvoiceUrl);
        window.location.assign(checkoutData.proRataInvoiceUrl);
        toast.info('Complete the payment, then return to confirm below.');
        moveToSecureStep(insertedDomains);
      }

      if (checkoutData?.requiresProRataPayment) {
        await refreshReviewedPricingSchedule();
        setIsLoading(false);
        moveToSecureStep(insertedDomains);
        return;
      }

      if (checkoutData?.redirect && checkoutData?.subscriptionId) {
        await refreshReviewedPricingSchedule();
        await supabase
          .from('domains')
          .update({ stripe_subscription_id: checkoutData.subscriptionId })
          .in('id', domainIds)
          .eq('user_id', domainOwnerUserId);
        toast.success('Domains added. Continue to secure your sites.');
        moveToSecureStep(insertedDomains);
        setIsLoading(false);
        return;
      }

      if (checkoutData?.url) {
        await refreshReviewedPricingSchedule();
        window.location.assign(checkoutData.url);
        toast.success('Redirecting to checkout. Secure your sites after payment completes.');
        moveToSecureStep(insertedDomains);
        return;
      }

      throw new Error('Failed to create subscription');
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Something went wrong');
      }
      setIsLoading(false);
    }
  };

  const handleVerifyInvoicePaid = async () => {
    if (!pendingInvoiceId) return;
    setCheckingInvoice(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-invoice-status', {
        body: { invoiceId: pendingInvoiceId },
      });
      if (error) throw error;
      if (data?.status === 'paid') {
        toast.success('Payment confirmed. Your domain will be activated.');
        setStep('secure');
        setOnboardingOpen(true);
      } else {
        toast.info(`Invoice is ${data?.status || 'pending'}. Please complete payment and try again.`);
      }
    } catch (err: any) {
      console.error('Invoice check failed:', err);
      toast.error(err.message || 'Could not verify payment status.');
    } finally {
      setCheckingInvoice(false);
    }
  };

  if (checking || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Add Domain | ShadowScan Portal</title>
          <meta
            name="description"
            content="Add one or multiple WordPress domains to ShadowScan for protection setup, monitored coverage where included, and monthly subscription activation."
          />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const validBulkCount = getValidBulkDomains().length;
  const confirmationQuantity = pendingSubmit === 'bulk' ? validBulkCount : 1;
  const activeQuantity = activeTab === "bulk" ? validBulkCount : 1;
  const baseTotalCost = pendingSubmit === 'bulk' ? validBulkCount * currentPlanPrice : currentPlanPrice;
  const couponApplies =
    !isDevAccount &&
    !isImpersonating &&
    couponStatus === "applied" &&
    couponDetails &&
    couponDetails.quantity === confirmationQuantity &&
    couponDetails.tier === selectedTier;
  const couponAppliesForTab =
    !isDevAccount &&
    !isImpersonating &&
    couponStatus === "applied" &&
    couponDetails &&
    couponDetails.quantity === activeQuantity &&
    couponDetails.tier === selectedTier;
  const baseMonthlyTotal = couponApplies ? couponDetails!.base_total : baseTotalCost;
  const discountedMonthlyTotal = couponApplies ? couponDetails!.discounted_total : baseMonthlyTotal;
  const proRataInfo = calculateProRata(discountedMonthlyTotal);
  const proRataBaseInfo = couponApplies ? calculateProRata(baseMonthlyTotal) : null;
  const trialEndsPreview = new Date(Date.now() + FREE_TRIAL_DAYS * 24 * 60 * 60 * 1000);
  const trialProRataInfo = calculateProRataFromDate(discountedMonthlyTotal, trialEndsPreview);
  const trialProRataBaseInfo = couponApplies ? calculateProRataFromDate(baseMonthlyTotal, trialEndsPreview) : null;
  const effectiveTotalCost = couponApplies ? couponDetails!.discounted_total : baseTotalCost;
  const couponDiscount = couponApplies ? couponDetails!.base_total - couponDetails!.discounted_total : 0;
  const cancellationFee = isDevAccount
    ? 0
    : hostingBaseSelected
      ? (selectedTier ? CANCELLATION_FEES[selectedTier] : CANCELLATION_FEES.basic)
      : (selectedTier ? CANCELLATION_FEES[selectedTier] : CANCELLATION_FEES.premium);
  const trialEligibleForTab = qualifiesForSelfServeTrial && activeQuantity > 0 && activeQuantity <= FREE_TRIAL_DOMAIN_LIMIT;
  const trialEligibleForPending = qualifiesForSelfServeTrial && confirmationQuantity > 0 && confirmationQuantity <= FREE_TRIAL_DOMAIN_LIMIT;
  const selfServeTrialActive = trialEligibleForPending;
  const trialPlanScopeLabel = isDevAccount
    ? 'approved dev domains'
    : selectedTier
    ? `${PUBLIC_TIER_LABELS[selectedTier]} domains`
    : 'eligible self-serve domains';
  const primaryActionLabel = isImpersonating
    ? 'Send Purchase Request'
    : trialEligibleForTab
      ? 'Start 7-Day Free Trial'
      : 'Continue to Payment Setup';
  const backLabel = step === 'domain'
    ? (isDevAccount ? 'Back' : 'Change Plan')
    : step === 'secure'
    ? 'Back to Domains'
    : 'Back';
  const handleBack = () => {
    if (step === 'domain') {
      if (isDevAccount) {
        navigate('/portal');
      } else {
        setStep('tier');
      }
    } else if (step === 'secure') {
      setStep('domain');
    } else {
      navigate('/portal');
    }
  };
  const onboardingPairingLink = onboardingDomain ? pairingLinks[onboardingDomain.id] : undefined;
  const paymentNeeded = onboardingDomain ? isPaymentNeeded(onboardingDomain.status) : false;
  const onboardingLifecycle = resolveHostedLifecycle(onboardingDomain, onboardingDomain?.managed_site);
  const onboardingLifecycleMeta = getHostedLifecycleMeta(onboardingLifecycle.state);
  const onboardingInstallTracking = resolveHostedPluginInstall(onboardingDomain?.managed_site);
  const onboardingAutomationTracking = resolveHostedAutomation(onboardingDomain?.managed_site);
  const secureDomainLabel =
    secureDomains.length > 1
      ? `${secureDomains.length} domains`
      : onboardingDomain?.domain_name || 'your domain';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Add Domain | ShadowScan Portal</title>
        <meta
          name="description"
          content="Enroll new WordPress domains into ShadowScan, submit admin access securely, and start protection coverage with subscription billing on the 1st."
        />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          {step !== 'secure' && (
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {backLabel}
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="text-xl font-mono font-bold">Add Domains</span>
          </div>
          {(selectedPlanLabel || isDevAccount) && (step === 'domain' || step === 'secure') && (
            <div className="ml-auto text-sm text-muted-foreground">
              Selected:{' '}
              <span className="text-primary font-semibold capitalize">
                {isDevAccount
                  ? `Dev (${formatCurrency(currentPlanPrice)})`
                  : selectedPlanLabel}
              </span>
            </div>
          )}
        </div>
      </header>

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-5xl">
        {portfolioPricingApplication ? (
          <div className="mb-6">
            <PortfolioPricingStatusCard
              application={portfolioPricingApplication}
              compact
              reviewedPricingSchedule={latestReviewedPricingSchedule}
            />
          </div>
        ) : null}

        {/* Step indicator */}
        {isDevAccount ? (
          <div className="flex items-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step === 'domain' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 'domain' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>1</div>
              <span className="font-medium">Add Domains</span>
            </div>
            <div className="flex-1 h-px bg-border" />
            <div className={`flex items-center gap-2 ${step === 'secure' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 'secure' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>2</div>
              <span className="font-medium">Secure Site</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step === 'tier' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 'tier' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>1</div>
              <span className="font-medium">Choose Plan</span>
            </div>
            <div className="flex-1 h-px bg-border" />
            <div className={`flex items-center gap-2 ${step === 'domain' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 'domain' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>2</div>
              <span className="font-medium">Add Domains</span>
            </div>
            <div className="flex-1 h-px bg-border" />
            <div className={`flex items-center gap-2 ${step === 'secure' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 'secure' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>3</div>
              <span className="font-medium">Secure Site</span>
            </div>
          </div>
        )}

        {step === 'domain' && (
          <div className="flex justify-end mb-4">
            <CurrencySelector />
          </div>
        )}

        {!isDevAccount && step === 'tier' ? (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-mono font-bold mb-2">Choose Your Protection Plan</h1>
              <p className="text-muted-foreground">Choose standalone monitored security or Basic Hosting with an optional Essential or Premium add-on.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  key: 'hosting-basic',
                  title: 'Basic Hosting',
                  price: 'AUD 12.99 / month',
                  description: 'Hosted WordPress onboarding with ShadowScan Guard setup.',
                  selected: hostingBaseSelected && !selectedTier,
                  onClick: () => handleHostingSelection(null),
                },
                {
                  key: 'hosting-essential',
                  title: 'Basic Hosting + Essential',
                  price: 'AUD 62.99 / month',
                  description: 'Hosted onboarding paired with Essential monitored security.',
                  selected: hostingBaseSelected && selectedTier === 'essential',
                  onClick: () => handleHostingSelection('essential'),
                },
                {
                  key: 'hosting-premium',
                  title: 'Basic Hosting + Premium',
                  price: 'AUD 197.99 / month',
                  description: 'Hosted onboarding paired with Premium analyst-led support.',
                  selected: hostingBaseSelected && selectedTier === 'premium',
                  onClick: () => handleHostingSelection('premium'),
                },
              ].map((option) => (
                <Card
                  key={option.key}
                  className={`cyber-card border-border/60 transition-colors ${option.selected ? 'border-primary ring-1 ring-primary/50' : 'hover:border-primary/40'}`}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{option.title}</CardTitle>
                    <CardDescription>{option.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="font-mono text-lg text-primary">{option.price}</div>
                    <Button variant={option.selected ? 'default' : 'outline'} className="w-full" onClick={option.onClick}>
                      {option.selected ? 'Selected' : 'Choose option'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <TierSelection onSelect={handleTierSelect} selectedTier={selectedTier || undefined} />
            
            <div className="flex justify-center pt-6">
              <Button 
                size="lg" 
                onClick={handleContinueToForm}
                disabled={!selectedTier && !hostingBaseSelected}
                className="min-w-[200px]"
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : step === 'domain' ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="single" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Single Domain
              </TabsTrigger>
              <TabsTrigger value="bulk" className="flex items-center gap-2" disabled={Boolean(upgradeTargetDomain)}>
                <Upload className="h-4 w-4" />
                Bulk Add
              </TabsTrigger>
            </TabsList>

            <TabsContent value="single">
              <Card className="cyber-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary" />
                    Domain Details
                  </CardTitle>
                  <CardDescription>
                    {upgradeTargetDomain
                      ? 'Upgrade the selected hosted site with Basic Hosting or a security add-on.'
                      : 'Add a site and choose the platform. Only WordPress is supported today.'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSingleSubmitClick} className="space-y-6">
                    {upgradeTargetDomain && (
                      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
                        <p className="font-medium text-foreground">Upgrading existing hosted site</p>
                        <p className="text-muted-foreground mt-1">
                          {upgradeTargetDomain.domain_name} will keep Basic Hosting and add the selected security service level.
                        </p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="domain">Domain Name</Label>
                      <Input
                        id="domain"
                        type="text"
                        value={domainName}
                        onChange={(e) => setDomainName(e.target.value)}
                        placeholder="example.com"
                        required
                        disabled={Boolean(upgradeTargetDomain)}
                        className="bg-muted/50 font-mono"
                      />
                      <p className="text-xs text-muted-foreground">Enter your website domain without http:// or www</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="platform">Platform</Label>
                      <select
                        id="platform"
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        disabled={Boolean(upgradeTargetDomain)}
                        className="w-full px-3 py-2 rounded-md border border-border/50 bg-background/50 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {PLATFORM_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {platform !== 'wordpress' && (
                        <p className="text-xs text-[hsl(var(--ss-warning))]">
                          {PLATFORM_OPTIONS.find((option) => option.value === platform)?.label || 'This platform'} is coming soon.
                        </p>
                      )}
                    </div>

                    <div className="p-4 border border-[hsl(var(--ss-warning)/0.3)] rounded-lg bg-[hsl(var(--ss-warning)/0.08)]">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-[hsl(var(--ss-warning))] mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-[hsl(var(--ss-warning))]">Platform Availability</p>
                          <p className="text-muted-foreground mt-1">
                            WordPress onboarding includes the Heartbeat plugin. Other platforms are coming soon.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="single-hosted-migration"
                          checked={hostedMigrationRequested}
                          onCheckedChange={(checked) => setHostedMigrationRequested(checked === true)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="single-hosted-migration">
                            This is a Valnex-hosted WordPress migration
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Use this only if you are buying a Valnex-hosted site and plan to migrate an existing WordPress install. Billing and hosting provisioning can start now, but plugin setup will wait until you confirm the migrated site is live.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-muted-foreground">
                          <span className="capitalize">{selectedPlanLabel || 'standard'}</span> plan - monthly fee
                        </span>
                        {couponAppliesForTab && couponDetails ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-muted-foreground line-through" title={estimatedLabel}>
                              {formatCurrency(currentPlanPrice)}
                            </span>
                            <span className="text-xl font-mono font-bold text-[hsl(var(--ss-success))]" title={estimatedLabel}>
                              {formatCurrency(couponDetails.discounted_total)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xl font-mono font-bold" title={estimatedLabel}>{formatCurrency(currentPlanPrice)}</span>
                        )}
                      </div>
                      {couponAppliesForTab && couponDetails && (
                        <p className="text-xs text-[hsl(var(--ss-success))] mb-4">
                          Coupon {couponCode.trim() || couponDetails.couponId} applied. Save {formatCurrency(couponDetails.base_total - couponDetails.discounted_total)} per month.
                        </p>
                      )}
                      {!isDevAccount && !isImpersonating && !hostingBaseSelected && (
                        <div className="rounded-lg border border-border/50 bg-muted/20 p-3 space-y-2 mb-4">
                          <Label htmlFor="coupon-code-single">Coupon code</Label>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Input
                              id="coupon-code-single"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              placeholder="Enter coupon code"
                              className="bg-muted/50 font-mono"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleApplyCoupon}
                              disabled={couponStatus === "checking"}
                            >
                              {couponStatus === "checking" ? "Checking..." : "Apply"}
                            </Button>
                          </div>
                          {couponMessage && (
                            <p className={`text-xs ${couponStatus === "invalid" ? "text-destructive" : "text-[hsl(var(--ss-success))]"}`}>
                              {couponMessage}
                            </p>
                          )}
                        </div>
                      )}
                      <Button type="submit" className="w-full" size="lg" disabled={isLoading || platform !== 'wordpress'}>
                        {isLoading ? 'Processing...' : primaryActionLabel}
                      </Button>
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        {isImpersonating
                          ? 'No charge is made now. The customer receives an email to complete purchase and activate billing.'
                          : selfServeTrialActive
                            ? `Card required before your ${FREE_TRIAL_LABEL}. No charge is made during the trial.`
                            : "You'll be redirected to set up your payment method. Monthly billing on the 1st."}
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="bulk">
              <Card className="cyber-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-primary" />
                    Bulk Add Domains
                  </CardTitle>
                  <CardDescription>
                    Add multiple domains at once. Import a CSV or fill in the details below. WordPress only for now.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleBulkSubmitClick} className="space-y-6">
                    <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="text-sm">
                          <p className="font-medium text-foreground">CSV import</p>
                          <p className="text-muted-foreground text-xs mt-1">
                            Template columns: <span className="font-mono">domainName, platform</span>
                          </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button type="button" variant="outline" onClick={downloadBulkTemplateCsv}>
                            <Download className="h-4 w-4 mr-2" />
                            Download template
                          </Button>
                          <Button type="button" onClick={() => csvFileInputRef.current?.click()}>
                            <Upload className="h-4 w-4 mr-2" />
                            Import CSV
                          </Button>
                        </div>
                      </div>
                      <input
                        ref={csvFileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          void importBulkCsvFile(file);
                        }}
                      />
                      <div className="text-xs text-muted-foreground">
                        Only WordPress rows can be submitted right now.
                      </div>
                    </div>

                    <div className="space-y-4">
                      {bulkDomains.map((domain, index) => (
                        <div key={index} className="p-4 border border-border/50 rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-medium text-sm flex items-center gap-2">
                              <Globe className="h-4 w-4 text-primary" />
                              Domain {index + 1}
                            </span>
                            {bulkDomains.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeBulkRow(index)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Domain Name</Label>
                              <Input
                                type="text"
                                value={domain.domainName}
                                onChange={(e) => updateBulkDomain(index, 'domainName', e.target.value)}
                                placeholder="example.com"
                                className="bg-muted/50 font-mono text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Platform</Label>
                              <select
                                value={domain.platform}
                                onChange={(e) => updateBulkDomain(index, 'platform', e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-border/50 bg-background/50 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                              >
                                {PLATFORM_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {domain.platform && domain.platform !== 'wordpress' && (
                                <p className="text-xs text-[hsl(var(--ss-warning))]">
                                  {PLATFORM_OPTIONS.find((option) => option.value === domain.platform)?.label || 'This platform'} is coming soon.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={addBulkRow}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Another Domain
                    </Button>

                    <div className="p-4 border border-[hsl(var(--ss-warning)/0.3)] rounded-lg bg-[hsl(var(--ss-warning)/0.08)]">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-[hsl(var(--ss-warning))] mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-[hsl(var(--ss-warning))]">Platform Availability</p>
                          <p className="text-muted-foreground mt-1">
                            Only WordPress domains can be submitted today. Other platforms are coming soon.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="bulk-hosted-migration"
                          checked={hostedMigrationRequested}
                          onCheckedChange={(checked) => setHostedMigrationRequested(checked === true)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="bulk-hosted-migration">
                            These are Valnex-hosted WordPress migrations
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Use this only if these domains are being moved onto Valnex-hosted WordPress. Billing and hosting provisioning can start now, but plugin setup will wait for migration confirmation per site.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-border">
                      {qualifiesForSelfServeTrial && validBulkCount > FREE_TRIAL_DOMAIN_LIMIT && (
                        <div className="mb-4 rounded-lg border border-[hsl(var(--ss-warning)/0.3)] bg-[hsl(var(--ss-warning)/0.08)] p-3 text-sm text-muted-foreground">
                          The {FREE_TRIAL_LABEL} is capped at {FREE_TRIAL_DOMAIN_LIMIT} {trialPlanScopeLabel}. This bulk request will use the standard paid flow instead.
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-muted-foreground">Domains to add</span>
                        <span className="font-mono font-bold">{validBulkCount}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-muted-foreground">
                          <span className="capitalize">{selectedPlanLabel || 'standard'}</span> plan - monthly total
                        </span>
                        {couponAppliesForTab && couponDetails ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-muted-foreground line-through">
                              <span title={estimatedLabel}>{formatCurrency(validBulkCount * currentPlanPrice)}</span>
                            </span>
                            <span className="text-xl font-mono font-bold text-[hsl(var(--ss-success))]">
                              <span title={estimatedLabel}>{formatCurrency(couponDetails.discounted_total)}</span>
                            </span>
                          </div>
                        ) : (
                          <span className="text-xl font-mono font-bold" title={estimatedLabel}>{formatCurrency(validBulkCount * currentPlanPrice)}</span>
                        )}
                      </div>
                      {couponAppliesForTab && couponDetails && (
                        <p className="text-xs text-[hsl(var(--ss-success))] mb-4">
                          Coupon {couponCode.trim() || couponDetails.couponId} applied. Save {formatCurrency(couponDetails.base_total - couponDetails.discounted_total)} per month.
                        </p>
                      )}
                      {!isDevAccount && !isImpersonating && !hostingBaseSelected && (
                        <div className="rounded-lg border border-border/50 bg-muted/20 p-3 space-y-2 mb-4">
                          <Label htmlFor="coupon-code-bulk">Coupon code</Label>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Input
                              id="coupon-code-bulk"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              placeholder="Enter coupon code"
                              className="bg-muted/50 font-mono"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleApplyCoupon}
                              disabled={couponStatus === "checking"}
                            >
                              {couponStatus === "checking" ? "Checking..." : "Apply"}
                            </Button>
                          </div>
                          {couponMessage && (
                            <p className={`text-xs ${couponStatus === "invalid" ? "text-destructive" : "text-[hsl(var(--ss-success))]"}`}>
                              {couponMessage}
                            </p>
                          )}
                        </div>
                      )}
                      <Button 
                        type="submit" 
                        className="w-full" 
                        size="lg" 
                        disabled={isLoading || validBulkCount === 0 || bulkDomains.some((domain) => domain.domainName.trim() && domain.platform !== 'wordpress')}
                      >
                        {isLoading ? 'Processing...' : primaryActionLabel}
                      </Button>
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        {isImpersonating
                          ? 'No charge is made now. The customer receives an email to complete purchase and activate billing.'
                          : selfServeTrialActive
                            ? `Card required before your ${FREE_TRIAL_LABEL}. No charge is made during the trial.`
                            : "You'll be redirected to set up your payment method. Monthly billing on the 1st."}
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-3xl font-mono font-bold mb-2">Secure Your Site</h1>
              <p className="text-muted-foreground">
                {secureDomains.length > 1
                  ? `Your domains are added. Start securing ${secureDomainLabel}.`
                  : 'Your domain is added. Next, connect and secure it.'}
              </p>
            </div>
            <Card className="cyber-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-[hsl(var(--ss-success))]" />
                  Secure Website Setup
                </CardTitle>
                <CardDescription>
                  {onboardingDomain?.status === 'trial'
                    ? `Your ${FREE_TRIAL_LABEL} is active. Pair the site now and billing will only begin after the trial ends.`
                    : paymentNeeded
                    ? (isImpersonating
                      ? 'You can configure setup now. Activation happens after the customer completes purchase.'
                      : 'You can begin setup now. Protection will activate after payment is confirmed.')
                    : 'Follow the guided steps to connect and protect your site.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Target:</span>
                    <span className="font-mono text-foreground">{secureDomainLabel}</span>
                  </div>
                  {secureDomains.length > 1 && onboardingDomain?.domain_name && (
                    <div className="flex items-center justify-between mt-2">
                      <span>Primary setup:</span>
                      <span className="font-mono text-foreground">{onboardingDomain.domain_name}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <span>Setup state:</span>
                    <span className="font-mono text-foreground">{onboardingLifecycleMeta.label}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span>Plugin:</span>
                    <span className="font-mono text-foreground">{onboardingLifecycle.pluginInstalled ? 'Detected' : SHADOWSCAN_WORDPRESS_PLUGIN.name}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span>Pairing:</span>
                    <span className="font-mono text-foreground">{onboardingLifecycle.pluginPaired ? 'Connected' : 'Not yet paired'}</span>
                  </div>
                  {onboardingInstallTracking.visibleInPortal && (
                    <div className="flex items-center justify-between mt-2">
                      <span>Hosted install:</span>
                      <span className="font-mono text-foreground">{onboardingInstallTracking.label}</span>
                    </div>
                  )}
                  {onboardingAutomationTracking.visibleInPortal && (
                    <div className="flex items-center justify-between mt-2">
                      <span>Hosted automation:</span>
                      <span className="font-mono text-foreground">{onboardingAutomationTracking.label}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {onboardingLifecycleMeta.description}
                </p>
                {onboardingDomain?.managed_site?.hosted_is_migration && !onboardingDomain?.managed_site?.hosted_migration_confirmed_at && (
                  <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-xs text-muted-foreground space-y-1">
                    <p className="text-foreground font-medium">Migration pending</p>
                    <p>Billing and hosting can be active while you migrate the existing WordPress site.</p>
                    <p>Protection is not live yet. After the migrated site loads on the hosted account, open setup and confirm migration complete to resume plugin setup.</p>
                  </div>
                )}
                {onboardingInstallTracking.visibleInPortal && (
                  <p className="text-xs text-muted-foreground">
                    {onboardingInstallTracking.description}
                    {onboardingInstallTracking.error ? ` ${onboardingInstallTracking.error}` : ''}
                  </p>
                )}
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    className="flex-1"
                    onClick={() => onboardingDomain && handleOpenOnboarding(onboardingDomain)}
                    disabled={!onboardingDomain}
                  >
                    Open Secure Website Setup
                  </Button>
                  {paymentNeeded && onboardingDomain && (
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleResumePayment(onboardingDomain.id)}
                      disabled={resumingDomainId === onboardingDomain.id}
                    >
                      {resumingDomainId === onboardingDomain.id
                        ? (isImpersonating ? 'Sending...' : 'Opening...')
                        : (isImpersonating ? 'Resend Customer Payment Email' : 'Resume Payment')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => navigate('/portal')}
                  >
                    Back to Dashboard
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Need to continue later? You can always finish setup from your dashboard.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {pendingInvoiceUrl && (
        <div className="fixed bottom-4 right-4 w-full max-w-md z-20">
          <Card className="shadow-lg border-primary/40">
            <CardHeader>
              <CardTitle className="text-base">Complete Payment</CardTitle>
              <CardDescription>Finish the pro-rata payment to activate your domain.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                A payment page should have opened. If not, <button className="text-primary underline" onClick={() => window.location.assign(pendingInvoiceUrl!)}>open the invoice</button>.
              </p>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={handleVerifyInvoicePaid}
                  disabled={checkingInvoice}
                >
                  {checkingInvoice ? 'Checking...' : "I've Paid"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.assign(pendingInvoiceUrl!)}
                >
                  View Invoice
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Service Agreement Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{isDevAccount ? `Service Agreement & ${REVIEWED_PRICING_SCHEDULE_TITLE}` : 'Service Agreement'}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  {isImpersonating
                    ? <>You are preparing <strong>{pendingSubmit === 'bulk' ? validBulkCount : 1} domain{(pendingSubmit === 'bulk' && validBulkCount !== 1) ? 's' : ''}</strong> for this customer with the <strong className="capitalize">{selectedPlanLabel || 'standard'}</strong> plan. No charge is made in this step.</>
                    : selfServeTrialActive
                      ? <>You are adding <strong>{pendingSubmit === 'bulk' ? validBulkCount : 1} domain{(pendingSubmit === 'bulk' && validBulkCount !== 1) ? 's' : ''}</strong> to your account with the <strong className="capitalize">{selectedPlanLabel || 'standard'}</strong> plan. Your <strong>{FREE_TRIAL_LABEL}</strong> starts after your saved payment method is confirmed.</>
                      : <>You are adding <strong>{pendingSubmit === 'bulk' ? validBulkCount : 1} domain{(pendingSubmit === 'bulk' && validBulkCount !== 1) ? 's' : ''}</strong> to your account with the <strong className="capitalize">{selectedPlanLabel || 'standard'}</strong> plan.</>}
                </p>

                {isDevAccount && (
                  <p className="text-sm text-muted-foreground">
                    The reviewed pricing schedule below records the exact custom dev rate, removal terms, and billing start terms for this activation. It forms part of your accepted billing terms alongside service agreement version <strong className="text-foreground">{SERVICE_AGREEMENT_VERSION}</strong>.
                  </p>
                )}
                
                <div className="p-4 bg-muted/50 rounded-lg space-y-3 text-sm">
                  <h4 className="font-semibold text-foreground">{isDevAccount ? `${REVIEWED_PRICING_SCHEDULE_TITLE}:` : 'Billing Terms:'}</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex justify-between">
                      <span>Plan:</span>
                      <span className="font-mono font-bold text-foreground capitalize">{selectedPlanLabel || 'standard'}</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Monthly fee per site:</span>
                      <span className="font-mono font-bold text-foreground" title={estimatedLabel}>{formatCurrency(currentPlanPrice)}</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Your monthly total:</span>
                      {couponApplies ? (
                        <span className="font-mono font-bold text-foreground">
                          <span className="line-through text-muted-foreground mr-2" title={estimatedLabel}>{formatCurrency(baseTotalCost)}</span>
                          <span className="text-[hsl(var(--ss-success))]">
                            <span title={estimatedLabel}>{formatCurrency(effectiveTotalCost)}</span>
                          </span>
                        </span>
                      ) : (
                        <span className="font-mono font-bold text-foreground" title={estimatedLabel}>{formatCurrency(effectiveTotalCost)}</span>
                      )}
                    </li>
                    {couponApplies && (
                      <li className="flex justify-between text-[hsl(var(--ss-success))]">
                        <span>Coupon discount:</span>
                        <span className="font-mono font-bold" title={estimatedLabel}>-{formatCurrency(couponDiscount)}</span>
                      </li>
                    )}
                    {!isDevAccount && (
                      <li className="flex justify-between text-[hsl(var(--ss-warning))]">
                        <span>Cancellation fee (per domain):</span>
                        <span className="font-mono font-bold" title={estimatedLabel}>
                          {selfServeTrialActive ? `${formatCurrency(cancellationFee)} after trial` : formatCurrency(cancellationFee)}
                        </span>
                      </li>
                    )}
                    {isDevAccount && (
                      <li className="flex justify-between">
                        <span>Agreement version:</span>
                        <span className="font-mono font-bold text-foreground">{SERVICE_AGREEMENT_VERSION}</span>
                      </li>
                    )}
                    {isDevAccount && reviewedPricingMinActiveDomains && (
                      <li className="flex justify-between">
                        <span>Minimum active paid domains to retain reviewed pricing:</span>
                        <span className="font-mono font-bold text-foreground">{reviewedPricingMinActiveDomains}</span>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg text-sm space-y-2">
                  <h4 className="font-semibold text-foreground">Key Terms:</h4>
                  <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Billing occurs on the <strong className="text-foreground">1st of each month</strong></li>
                    <li>Month-to-month service, cancel anytime</li>
                    {selfServeTrialActive ? (
                      <>
                        <li>Your <strong className="text-foreground">{FREE_TRIAL_LABEL}</strong> applies to up to {FREE_TRIAL_DOMAIN_LIMIT} {trialPlanScopeLabel}.</li>
                        <li>Card required before the trial starts. No charge is made during the trial.</li>
                        <li>Cancel before the trial ends and you will not be charged.</li>
                        <li>Additional domains outside the 3-domain limit follow the standard paid flow.</li>
                        <li>Estimated next monthly charge on the 1st after trial: <strong className="text-foreground" title={estimatedLabel}>{formatCurrency(effectiveTotalCost)}</strong></li>
                      </>
                    ) : (
                      <>
                        {isDevAccount && (
                          <>
                            <li>No cancellation fee applies if you remove a domain covered by this reviewed pricing schedule.</li>
                            <li>No partial-month refund is issued for the current billing period if you remove a reviewed-pricing domain.</li>
                            <li>Future billing for a removed reviewed-pricing domain stops from the next billing cycle.</li>
                            {reviewedPricingMinActiveDomains ? (
                              <li>Reviewed pricing may be reassessed if active paid reviewed-pricing domains remain below {reviewedPricingMinActiveDomains} for one full billing cycle.</li>
                            ) : null}
                          </>
                        )}
                        {!isDevAccount && (
                          <>
                            <li><span title={estimatedLabel}>{formatCurrency(cancellationFee)}</span> cancellation fee per domain will be charged to your payment method</li>
                            <li>No partial-month refunds apply if you remove a domain after activation unless required by law.</li>
                          </>
                        )}
                        <li>{isImpersonating ? 'Customer receives an email and must complete purchase to activate service.' : 'Payment method required to activate service.'}</li>
                        <li>If billing pauses, protection remains active. If billing is not resumed within 7 days, automated updates and configuration changes are temporarily paused until billing resumes.</li>
                        <li>Next monthly charge on the 1st: <strong className="text-foreground" title={estimatedLabel}>{formatCurrency(effectiveTotalCost)}</strong></li>
                      </>
                    )}
                  </ul>
                </div>

                {!isImpersonating && (
                  <div className="p-4 bg-muted/50 rounded-lg space-y-2 text-sm border border-primary/20">
                    <div className="text-sm font-semibold text-foreground">Today You Pay:</div>
                    {selfServeTrialActive ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Free-trial charge</span>
                          <span className="font-mono font-bold text-foreground">AUD $0.00</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Trial ends</span>
                          <span className="font-mono font-bold text-foreground">{formatTrialEndLabel(trialEndsPreview.toISOString())}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Estimated first charge after trial ({trialProRataInfo.daysRemaining} day(s) until next cycle)</span>
                          {couponApplies && trialProRataBaseInfo ? (
                            <span className="font-mono font-bold text-foreground" title={estimatedLabel}>
                              <span className="line-through text-muted-foreground mr-2">{formatCurrency(trialProRataBaseInfo.amount)}</span>
                              <span className="text-[hsl(var(--ss-success))]">{formatCurrency(trialProRataInfo.amount)}</span>
                            </span>
                          ) : (
                            <span className="font-mono font-bold text-foreground" title={estimatedLabel}>{formatCurrency(trialProRataInfo.amount)}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Pro-Rata Charge ({proRataInfo.daysRemaining} day(s) until next cycle)</span>
                        {couponApplies && proRataBaseInfo ? (
                          <span className="font-mono font-bold text-foreground" title={estimatedLabel}>
                            <span className="line-through text-muted-foreground mr-2">{formatCurrency(proRataBaseInfo.amount)}</span>
                            <span className="text-[hsl(var(--ss-success))]">{formatCurrency(proRataInfo.amount)}</span>
                          </span>
                        ) : (
                          <span className="font-mono font-bold text-foreground" title={estimatedLabel}>{formatCurrency(proRataInfo.amount)}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-start space-x-3 pt-2">
                  <Checkbox 
                    id="terms" 
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                  />
                  <label 
                    htmlFor="terms" 
                    className="text-sm text-muted-foreground cursor-pointer leading-tight"
                  >
                    {isImpersonating
                      ? 'I confirm this setup request will email the customer to complete purchase.'
                      : selfServeTrialActive
                          ? <>I agree to the billing terms and understand that the <strong className="text-foreground">{FREE_TRIAL_LABEL}</strong> is limited to {FREE_TRIAL_DOMAIN_LIMIT} {trialPlanScopeLabel}, that card setup is required before it starts, and that no cancellation fee applies if I remove a domain before the trial ends.</>
                        : isDevAccount
                          ? <>I agree to this <strong className="text-foreground">{REVIEWED_PRICING_SCHEDULE_TITLE.toLowerCase()}</strong> and understand that it records the custom dev pricing, no-fee removal terms, no partial-month refund policy, and billing start terms accepted for this activation.</>
                          : <>I agree to the billing terms and understand that a <strong className="text-foreground" title={estimatedLabel}>{formatCurrency(cancellationFee)} cancellation fee</strong> applies per domain if I remove a domain from my account, and that no partial-month refund applies after activation unless required by law.</>}
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmAdd} 
              disabled={isLoading || !agreedToTerms}
            >
              {isLoading ? 'Processing...' : (isImpersonating ? 'Agree & Send Purchase Request' : 'Agree & Set Up Payment')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pairingDialogOpen} onOpenChange={setPairingDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Pairing code</AlertDialogTitle>
            <AlertDialogDescription>
              Enter this code in the ShadowScan plugin to link your site. Codes expire after 10 minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <Label htmlFor="pairing-code">Pairing code</Label>
            <Input id="pairing-code" readOnly value={pairingDialogCode} />
            <Button type="button" variant="outline" onClick={handleCopyPairingCode}>
              Copy
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogAction type="button">Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SecureWebsiteOnboardingDialog
        open={onboardingOpen}
        onOpenChange={handleOnboardingOpenChange}
        domain={onboardingDomain}
        managedSite={onboardingDomain?.managed_site ?? null}
        pairingLink={onboardingPairingLink}
        paymentNeeded={paymentNeeded}
        selectedTier={isDevAccount ? undefined : (selectedTier || undefined)}
        onGeneratePairingLink={onboardingDomain ? (token) => handleGeneratePairingLink(onboardingDomain, token) : undefined}
        onResumePayment={onboardingDomain ? () => handleResumePayment(onboardingDomain.id) : undefined}
        paymentActionLabel={isImpersonating ? 'Resend customer payment email' : 'Complete billing'}
        onWpAdminUrlUpdated={(domainId, wpAdminUrl) => {
          setOnboardingDomain((prev) =>
            prev && prev.id === domainId ? { ...prev, wp_admin_url: wpAdminUrl } : prev,
          );
          setSecureDomains((prev) =>
            prev.map((domain) =>
              domain.id === domainId ? { ...domain, wp_admin_url: wpAdminUrl } : domain,
            ),
          );
        }}
      />
      <CurrencyDock />
    </div>
  );
}
